import { Text } from "@nbs/ui";
import {
  formatCoverageCellTooltip,
  formatCoverageStatusLabel,
} from "@/features/analytics/utils/domain-labels";

export type NbsHeatmapCell = {
  readonly row: string;
  readonly col: string;
  /** Sous-libellé de colonne (ex. année), affiché sous le libellé principal. */
  readonly colMeta?: string;
  readonly value: number;
  readonly status?: string;
};

export type NbsHeatmapProps = {
  readonly cells: readonly NbsHeatmapCell[];
  readonly emptyMessage?: string;
};

function tone(status?: string, value?: number): string {
  if (status === "available" || (value ?? 0) > 0) return "nbs-heatmap__cell--ok";
  if (status === "to_validate") return "nbs-heatmap__cell--warn";
  return "nbs-heatmap__cell--miss";
}

function colKey(col: string, colMeta?: string): string {
  return colMeta ? `${col}__${colMeta}` : col;
}

function colLabel(col: string, colMeta?: string): string {
  return colMeta ? `${col} ${colMeta}` : col;
}

export function NbsHeatmap({
  cells,
  emptyMessage = "Aucune cellule de couverture disponible.",
}: NbsHeatmapProps) {
  if (cells.length === 0) {
    return (
      <div className="nbs-chart-container__empty">
        <Text>{emptyMessage}</Text>
      </div>
    );
  }

  const rows = [...new Set(cells.map((c) => c.row))];
  const cols = [
    ...new Map(
      cells.map((c) => [colKey(c.col, c.colMeta), { col: c.col, colMeta: c.colMeta }] as const),
    ).values(),
  ];

  return (
    <div className="nbs-heatmap" role="table" aria-label="Matrice de couverture">
      <div
        className="nbs-heatmap__grid"
        style={{
          gridTemplateColumns: `minmax(11.5rem, 16rem) repeat(${cols.length}, minmax(3.75rem, 1fr))`,
        }}
      >
        <div className="nbs-heatmap__corner" aria-hidden="true" />
        {cols.map(({ col, colMeta }) => {
          const label = colLabel(col, colMeta);
          return (
            <div
              key={colKey(col, colMeta)}
              className="nbs-heatmap__colhead"
              role="columnheader"
              title={label}
            >
              <span className="nbs-heatmap__colhead-label">{col}</span>
              {colMeta ? <span className="nbs-heatmap__colhead-meta">{colMeta}</span> : null}
            </div>
          );
        })}
        {rows.map((row) => (
          <div key={row} className="nbs-heatmap__row" role="row">
            <div className="nbs-heatmap__rowhead" role="rowheader" title={row}>
              <span className="nbs-heatmap__rowhead-label">{row}</span>
            </div>
            {cols.map(({ col, colMeta }) => {
              const cell = cells.find(
                (c) => c.row === row && c.col === col && c.colMeta === colMeta,
              );
              const value = cell?.value ?? 0;
              const scope = colLabel(col, colMeta);
              const tooltip =
                value > 0
                  ? formatCoverageCellTooltip({
                      row,
                      col: scope,
                      value,
                      ...(cell?.status != null ? { status: cell.status } : {}),
                    })
                  : undefined;
              const statusLabel = formatCoverageStatusLabel(cell?.status ?? "absent");
              const ariaLabel =
                value > 0 ? tooltip : `${row} — ${scope} : aucune mesure (${statusLabel})`;

              return (
                <div
                  key={`${row}-${colKey(col, colMeta)}`}
                  className={`nbs-heatmap__cell ${tone(cell?.status, value)}${
                    value > 0 ? " nbs-heatmap__cell--has-tip" : ""
                  }`}
                  role="cell"
                  tabIndex={value > 0 ? 0 : undefined}
                  title={tooltip}
                  aria-label={ariaLabel}
                >
                  <span className="nbs-heatmap__cell-value">{value}</span>
                  {tooltip ? (
                    <span className="nbs-heatmap__tooltip" role="tooltip">
                      {tooltip}
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
